/* eslint-disable require-jsdoc */
import { useEffect, useMemo, useRef, useState } from "react";
import { getCarouselScrollerMeta } from "../../../utils/carousel/getCarouselScrollerMeta";

const MOBILE_VISIBLE_SLIDES = 2.3;
const SLIDES_TO_SCROLL = 1;

/**
 * Tracks carousel slide index for CarouselScroller (slick on desktop, native scroll on mobile).
 * @param {Object} params
 * @param {boolean} params.isMobile
 * @param {number} params.slidesToShow
 * @param {number} params.totalItems
 * @returns {Object}
 */
function useWidgetCarouselScroller({ isMobile, slidesToShow, totalItems }) {
  const mobileScrollRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const effectiveSlidesToShow = isMobile ? MOBILE_VISIBLE_SLIDES : slidesToShow;

  const { totalSlides, showScroller } = useMemo(
    () => getCarouselScrollerMeta(totalItems, effectiveSlidesToShow, SLIDES_TO_SCROLL),
    [effectiveSlidesToShow, totalItems],
  );

  useEffect(() => {
    setCurrentSlide(0);
  }, [totalItems]);

  useEffect(() => {
    if (!isMobile || !showScroller) {
      return undefined;
    }

    const element = mobileScrollRef.current;
    if (!element) {
      return undefined;
    }

    let rafId = 0;

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const maxScroll = element.scrollWidth - element.clientWidth;
        if (maxScroll <= 0) {
          setCurrentSlide(0);
          return;
        }

        const progress = Math.min(1, Math.max(0, element.scrollLeft / maxScroll));
        const slideIndex = Math.round(progress * (totalSlides - 1));
        setCurrentSlide(slideIndex);
      });
    };

    element.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(handleScroll) : null;
    resizeObserver?.observe(element);
    handleScroll();

    return () => {
      cancelAnimationFrame(rafId);
      element.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      resizeObserver?.disconnect();
    };
  }, [isMobile, showScroller, totalSlides]);

  return {
    mobileScrollRef,
    currentSlide,
    setCurrentSlide,
    totalSlides,
    showScroller,
    effectiveSlidesToShow,
    slidesToScroll: SLIDES_TO_SCROLL,
  };
}

export default useWidgetCarouselScroller;
